import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, FileTextIcon, SnowflakeIcon, StethoscopeIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { ProductCard } from '../components/shop/ProductCard';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
import { Eyebrow } from '../components/ui/Eyebrow';
import { SectionHeading } from '../components/ui/SectionHeading';
import { products } from '../data/products';
import { ProductCategory } from '../types/content';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
const filters: ('All' | ProductCategory)[] = ['All', 'FDA-approved', 'Peptide', 'Compounded'];
const assurances = [{
  icon: StethoscopeIcon,
  title: 'Prescription, not purchase',
  detail: 'Adding a medication starts a clinical review. A licensed clinician confirms it is appropriate before anything is dispensed or charged.'
}, {
  icon: FileTextIcon,
  title: 'Named pharmacy and lot',
  detail: 'Every shipment lists the US-licensed pharmacy that filled it, the lot number and the beyond-use date.'
}, {
  icon: SnowflakeIcon,
  title: 'Cold-chain included',
  detail: 'Temperature-controlled shipping is included in the published price, not added at checkout.'
}];
export function Shop() {
  const [active, setActive] = useState<'All' | ProductCategory>('All');
  useDocumentMeta({
    title: 'Pharmacy — Republic',
    description: 'FDA-approved medications and US-compounded peptides with the monthly price, form, strength and dispensing pharmacy stated up front.'
  });
  const featured = products[0];
  const rest = products.slice(1);
  const visible = active === 'All' ? rest : rest.filter(product => product.category === active);
  return <main data-id="element-594" data-mp="ixzhy5rgtjit">
      <PageHeader eyebrow="Pharmacy" title="Every medication, every price, on one page." intro="FDA-approved medications and US-compounded peptides at the rate Republic pays plus a fixed handling fee. Prescription required — the price you see is the price you are charged." data-id="element-595" data-mp="iy95znsy9glv" />

      <section className="bg-cream" data-id="element-596" data-mp="2twaco0madkih">
        <div className="mx-auto max-w-shell px-6 pb-16 pt-12 md:px-8 md:pb-20" data-id="element-597" data-mp="2tv917v1cyw08">
          <div className="rule grid items-center gap-10 pt-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16" data-id="element-598" data-mp="2owpgtz2exs9z">
            <img src={featured.image} alt={`${featured.name}, ${featured.form}`} className="aspect-[4/3] w-full bg-stone-light object-cover" data-id="element-599" data-mp="2173dr0ix6swp" />
            <div data-id="element-600" data-mp="2axy68mmreoql">
              <Eyebrow className="text-brick" data-id="element-601" data-mp="2ayhkntd7auzv">Most requested · {featured.status}</Eyebrow>
              <h2 className="mt-4 font-serif text-4xl leading-[1.04] text-ink md:text-[3.5rem]" data-id="element-602" data-mp="266fztj1yvvy8">
                {featured.name}
              </h2>
              <p className="mt-5 max-w-lg text-lg leading-relaxed text-ink-soft" data-id="element-603" data-mp="1if291hjgs8fd">
                {featured.tagline}
              </p>
              <dl className="rule mt-8 grid gap-6 pt-6 sm:grid-cols-3" data-id="element-604" data-mp="3p4vv8tq4dr9u">
                <div data-id="element-605" data-mp="3p3kqaqkxudh5">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-606" data-mp="3twnnd8oxc523">Price</dt>
                  <dd className="mt-2 font-serif text-3xl text-ink" data-id="element-607" data-mp="3g5936yja5xtg">
                    ${featured.price}
                    <span className="ml-2 font-sans text-sm text-ink-muted" data-id="element-608" data-mp="3k5j96b72oo8b">
                      {featured.cadence}
                    </span>
                  </dd>
                </div>
                <div data-id="element-609" data-mp="gq41vhq2wsqv">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-610" data-mp="goj8hvfi3ful">Form</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-611" data-mp="2ornhaqkgz73">{featured.form}</dd>
                </div>
                <div data-id="element-612" data-mp="7ncohcdn7mwl">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-613" data-mp="7r0ew2nsjt73">
                    Strength
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-614" data-mp="2sferwlem0hbb">{featured.strength}</dd>
                </div>
              </dl>
              <div className="mt-8 flex flex-wrap gap-4" data-id="element-615" data-mp="37q31ykd1g0iq">
                <Button to={`/shop/${featured.slug}`} data-id="element-616" data-mp="32xtd77nnt6i1">
                  View details
                  <ArrowRightIcon className="h-4 w-4" data-id="element-617" data-mp="2xygsasgcm9i3" />
                </Button>
                <Button to="/get-started" variant="secondary" data-id="element-618" data-mp="2txdesclrwpk1">
                  Start intake
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-619" data-mp="3loji11wsj3dj">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-620" data-mp="3lpm50yqzslo6">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-621" data-mp="3hmg3g4etrio1">
            <SectionHeading eyebrow="Catalogue" title="Priced by the month, not by the dose." data-id="element-622" data-mp="3cnv4077ed30n" />
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter medications" data-id="element-623" data-mp="3cq7lykd5nddp">
              {filters.map(filter => <button key={filter} type="button" role="tab" aria-selected={active === filter} onClick={() => setActive(filter)} className={`px-5 py-2.5 text-sm transition-colors duration-150 ease-republic ${active === filter ? 'bg-ink text-cream-light' : 'border border-ink/20 text-ink-soft hover:border-ink hover:text-ink'}`} data-id="element-624" data-mp="217obfsllcdf9">
                  {filter}
                </button>)}
            </div>
          </div>

          <ul className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-625" data-mp="14ich4144gbz1">
            {visible.map(product => <ProductCard key={product.slug} product={product} data-id="element-626" data-mp="1dgxuziynf2th" />)}
          </ul>

          {visible.length === 0 && <p className="rule mt-12 pt-10 text-base text-ink-soft" data-id="element-627" data-mp="3b7hbldj9htw4">
              Nothing in this category yet. Message the care team and we will tell you what is coming.
            </p>}
        </div>
      </section>

      <section className="bg-ink text-cream-light" data-id="element-628" data-mp="mecdelldmaa9">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-629" data-mp="hezjxi0024yp">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20" data-id="element-630" data-mp="hh2iiqf7x6f1">
            <SectionHeading eyebrow="How the pharmacy works" tone="cream" title="Nothing ships from a checkout." data-id="element-631" data-mp="vftrjyf841wb" />
            <ul className="grid gap-10 sm:grid-cols-3 sm:gap-8" data-id="element-632" data-mp="15bn1da1k5hae">
              {assurances.map(item => <li key={item.title} data-id="element-633" data-mp="10csm2i2suav1">
                  <item.icon className="h-6 w-6 text-stone" strokeWidth={1.4} data-id="element-634" data-mp="10br742inx481" />
                  <h3 className="mt-5 font-serif text-xl leading-snug text-cream-light" data-id="element-635" data-mp="1f1oonzm36vo9">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70" data-id="element-636" data-mp="1o2mvsqrtnjb2">{item.detail}</p>
                </li>)}
            </ul>
          </div>
          <p className="rule-light mt-14 max-w-4xl pt-8 text-xs leading-relaxed text-cream/55" data-id="element-637" data-mp="1j209bceg8vzq">
            Compounded medications and peptides are not FDA-approved products. They are prepared by
            US-licensed 503A and 503B pharmacies and dispensed only against a prescription written by
            a clinician licensed in your state.{' '}
            <Link to="/about" className="underline hover:text-cream" data-id="element-638" data-mp="1xq0ks1cavn8b">
              Read our clinical standards
            </Link>
            .
          </p>
        </div>
      </section>

      <CtaBand title="Prices published. Protocol first." intro="Start the intake and your clinician will confirm which of these is appropriate before you are charged for anything." data-id="element-639" data-mp="1oqlx19nipqri" />
    </main>;
}